import React from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import type { MapDriver } from './useDriverMapData';
import DriverSelect from '@features/drivers/components/DriverSelect';
import type { DriverSelectDriver } from '@features/drivers/components/DriverSelect';

import { css } from '@emotion/css';
import { Theme, useTheme, Typography, IconButton, Tooltip } from '@mui/material';
import { BiRefresh } from 'react-icons/bi';

dayjs.extend(relativeTime);

// TYPES --------------------------------------------------------------------- //

interface DriverSelectionSidebarProps {
  drivers: MapDriver[];
  selectedDriverId: number | null;
  onSelectDriver: (driverId: number | null) => void;
  onRefresh: () => void;
}

// COMPONENT ----------------------------------------------------------------- //

const DriverSelectionSidebar = ({
  drivers,
  selectedDriverId,
  onSelectDriver,
  onRefresh,
}: DriverSelectionSidebarProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const renderExtra = React.useCallback((driver: DriverSelectDriver) => {
    const mapDriver = driver as MapDriver;

    const lastPingTime = mapDriver.gpslocations?.[0]?.time;
    const lastSeen = lastPingTime ? dayjs(lastPingTime).fromNow() : null;
    const moveCount = mapDriver.moveCount || 0;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        {lastSeen ? (
          <Tooltip title={`Last seen ${lastSeen}`} arrow>
            <Typography variant='body2' sx={{ fontSize: 11, color: theme.palette.text.disabled }}>
              {lastSeen}
            </Typography>
          </Tooltip>
        ) : null}
        {moveCount > 0 ? (
          <Tooltip title={`${moveCount} moves`} arrow>
            <Typography
              variant='body2'
              sx={{ fontSize: 11, fontWeight: 600, color: theme.palette.text.secondary }}
            >
              {moveCount}
            </Typography>
          </Tooltip>
        ) : null}
      </div>
    );
  }, [theme]);

  return (
    <div className={cls.sidebar}>
      <div className={cls.header}>
        <div>
          <Typography variant='body1' sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            Drivers
          </Typography>
          <Typography variant='body2' sx={{ color: theme.palette.text.secondary, fontSize: 12 }}>
            {drivers.filter(d => d.status === 'online').length} online &middot; {drivers.length} total
          </Typography>
        </div>
        <Tooltip title='Refresh' arrow>
          <IconButton size='small' onClick={onRefresh}>
            <BiRefresh size={18} />
          </IconButton>
        </Tooltip>
      </div>

      <div className={cls.body}>
        <DriverSelect
          drivers={drivers}
          selectedDriverId={selectedDriverId}
          onSelect={onSelectDriver}
          showSummary={false}
          renderExtra={renderExtra}
        />
      </div>
    </div>
  );
};

// STYLES -------------------------------------------------------------------- //

const useStyles = (theme?: Theme) => ({
  sidebar: css`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    background-color: ${theme?.palette?.background?.paper};
  `,
  header: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid ${theme?.palette?.divider};
    flex-shrink: 0;
  `,
  body: css`
    flex: 1;
    overflow: hidden;
    padding: 10px 6px 6px;
  `,
});

export default DriverSelectionSidebar;
